import { APP_OWNER_SHARE, NGO_DISCOUNT, PERIODS, VAT_RATE, type Segment, type Tier } from './catalog';

export interface Quote {
  /** Months the package runs, including free bonus months. */
  months: number;
  /** List price for the whole period, before offers. */
  list: number;
  /** Price after period and NGO offers, before VAT. */
  paid: number;
  perMonth: number;
  savePct: number;
  /** Cost per 1,000 views. */
  cpm: number;
  vat: number;
  total: number;
  viewsPerMonth: number;
}

export function quote(tier: Tier, stopIndex: number, periodMonths: number, segment: Segment): Quote {
  const period = PERIODS.find((p) => p.months === periodMonths);
  if (!period) throw new RangeError('Unsupported package period.');
  if (!Number.isSafeInteger(stopIndex) || stopIndex < 0 || stopIndex >= tier.prices.length) {
    throw new RangeError('Choose a valid package view allowance.');
  }
  if (segment !== 'biz' && segment !== 'ngo') throw new RangeError('Unsupported sponsor segment.');
  const idx = stopIndex;
  const monthly = tier.prices[idx];
  if (!Number.isFinite(monthly) || monthly <= 0 || !Number.isFinite(tier.stops[idx]) || tier.stops[idx] <= 0) {
    throw new RangeError('Package prices and view allowances must be positive numbers.');
  }
  const months = period.months + (period.bonusMonths ?? 0);
  const list = monthly * months;
  let paid = monthly * period.months * (1 - (period.discount ?? 0));
  if (segment === 'ngo') paid *= 1 - NGO_DISCOUNT;
  paid = Math.round(paid);
  const vat = Math.round(paid * VAT_RATE);
  const viewsPerMonth = tier.stops[idx] * 1000;
  return {
    months,
    list,
    paid,
    perMonth: paid / months,
    savePct: Math.round((1 - paid / list) * 100),
    cpm: paid / (tier.stops[idx] * months),
    vat,
    total: paid + vat,
    viewsPerMonth,
  };
}

/** Split of package money (before VAT): Loyalty keeps the rest after app owners. */
export function revenueSplit(paid: number, viewsByApp: Record<string, number>) {
  if (!Number.isFinite(paid) || paid < 0 || paid > Number.MAX_SAFE_INTEGER) {
    throw new RangeError('Revenue must be a non-negative, safely representable number.');
  }
  const entries = Object.entries(viewsByApp);
  if (entries.some(([, views]) => !Number.isFinite(views) || views < 0)) {
    throw new RangeError('Views must be non-negative numbers.');
  }
  // Preserve the existing demo's whole-dollar policy and allocate rounding remainders.
  const ownersPool = Math.round(paid * APP_OWNER_SHARE);
  const totalViews = Object.values(viewsByApp).reduce((s, v) => s + v, 0);
  if (!Number.isFinite(totalViews) || totalViews > Number.MAX_SAFE_INTEGER) throw new RangeError('View total is too large.');
  const allocations = entries.map(([app, views], index) => {
    const exact = totalViews ? ownersPool * (views / totalViews) : 0;
    return { app, index, amount: Math.floor(exact), remainder: exact - Math.floor(exact) };
  });
  if (totalViews > 0) {
    const remainder = ownersPool - allocations.reduce((sum, entry) => sum + entry.amount, 0);
    const ranked = [...allocations].sort((left, right) => right.remainder - left.remainder || left.index - right.index);
    for (let index = 0; index < remainder; index += 1) ranked[index].amount += 1;
  }
  const owners = Object.fromEntries(allocations.map(({ app, amount }) => [app, amount]));
  return { loyalty: paid - ownersPool, ownersPool, owners, unallocated: totalViews ? 0 : ownersPool };
}

export const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
export const num = (n: number) => Math.round(n).toLocaleString('en-US');
export const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
